import { Button, Badge } from "@aix/ui"
import { useState } from "react"
import toast from "react-hot-toast"
import type { Coach, QuarterlyPlan } from "../../../types/coach-os.types"
import { trpc } from "../../../lib/trpc"
import QuarterlyPlanModal from "./QuarterlyPlanModal"
import AdjustPlanModal from "./AdjustPlanModal"

interface OverviewTabProps {
  coach: Coach
  refetchCoach: () => void
}

export default function OverviewTab({ coach, refetchCoach }: OverviewTabProps) {
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false)
  const [showQuarterlyPlanModal, setShowQuarterlyPlanModal] = useState(false)
  const [selectedPlanForAdjustment, setSelectedPlanForAdjustment] = useState<string | null>(null)

  // Fetch quarterly plans for this coach
  const {
    data: quarterlyPlansData,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = trpc.plans.quarterlyPlans.list.useQuery({
    coachId: coach.id,
    limit: 10,
  })

  // Fetch weekly plans count
  const { data: weeklyPlansData } = trpc.plans.weeklyPlans.list.useQuery({
    coachId: coach.id,
    limit: 100,
  })

  // Fetch trackables count
  const { data: trackablesData } = trpc.trackables.listCoachTrackables.useQuery({
    coachId: coach.id,
  })

  // Get the most recent plan
  const latestPlan = quarterlyPlansData?.quarterlyPlans?.[0]
  const weeklyPlansCount = weeklyPlansData?.weeklyPlans.length || 0
  const trackablesCount = trackablesData?.trackables.length || 0

  // Generate weekly plan mutation
  const generateWeeklyPlanMutation = trpc.plans.weeklyPlans.generate.useMutation()

  const handleGenerateWeeklyPlan = async () => {
    setIsGeneratingPlan(true)
    try {
      // Calculate current week number and dates
      const now = new Date()
      const startOfYear = new Date(now.getFullYear(), 0, 1)
      const weekNumber = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7)

      const getStartOfWeek = (date: Date) => {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() - day
        return new Date(d.setDate(diff))
      }

      const getEndOfWeek = (date: Date) => {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() + (6 - day)
        return new Date(d.setDate(diff))
      }

      const weekStartDate = getStartOfWeek(now).toISOString().split("T")[0]
      const weekEndDate = getEndOfWeek(now).toISOString().split("T")[0]

      await generateWeeklyPlanMutation.mutateAsync({
        coachId: coach.id,
        weekNumber,
        weekStartDate,
        weekEndDate,
        phaseName: "Current Phase",
      })

      toast.success("Weekly plan generated successfully!")
      refetchCoach()
    } catch (error: any) {
      toast.error(`Error generating plan: ${error.message}`)
    } finally {
      setIsGeneratingPlan(false)
    }
  }

  const handleAdjustPlan = () => {
    // Get the most recent active or upcoming weekly plan
    if (!weeklyPlansData?.weeklyPlans || weeklyPlansData.weeklyPlans.length === 0) {
      toast.error("No weekly plan to adjust. Generate a plan first.")
      return
    }

    const activePlan = weeklyPlansData.weeklyPlans.find((p) => p.status === "active" || p.status === "upcoming")
    if (!activePlan) {
      toast.error("No active or upcoming plan to adjust")
      return
    }

    setSelectedPlanForAdjustment(activePlan.id)
  }

  const handleQuarterlyPlanSuccess = () => {
    void refetchPlans()
    refetchCoach()
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Coach Info Card */}
      <div className="bg-secondary rounded-lg p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-foreground mb-3 md:mb-4">Coach Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Type</p>
            <p className="text-foreground font-medium capitalize">{coach.type}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Tone</p>
            <p className="text-foreground font-medium capitalize">{coach.tone}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <p className="text-foreground font-medium capitalize">{coach.status}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Created</p>
            <p className="text-foreground font-medium">{new Date(coach.createdAt).toLocaleDateString()}</p>
          </div>
          {coach.startDate && (
            <div>
              <p className="text-sm text-muted-foreground">Start Date</p>
              <p className="text-foreground font-medium">{new Date(coach.startDate).toLocaleDateString()}</p>
            </div>
          )}
          {coach.targetEndDate && (
            <div>
              <p className="text-sm text-muted-foreground">Target End Date</p>
              <p className="text-foreground font-medium">{new Date(coach.targetEndDate).toLocaleDateString()}</p>
            </div>
          )}
          {coach.actualEndDate && (
            <div>
              <p className="text-sm text-muted-foreground">Actual End Date</p>
              <p className="text-foreground font-medium">{new Date(coach.actualEndDate).toLocaleDateString()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Quarterly Plan Section */}
      <div className="bg-secondary rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-foreground">Quarterly Plan</h2>
          <Button onClick={() => setShowQuarterlyPlanModal(true)} disabled={coach.status !== "active"}>
            + Generate Quarterly Plan
          </Button>
        </div>
        <div className="text-muted-foreground">
          <p className="mb-4">Quarterly plans help structure your goals into manageable phases over 12-13 weeks.</p>

          {plansLoading ? (
            <div className="bg-background border border-border rounded-lg p-6 text-center">
              <p className="text-sm text-muted-foreground">Loading plans...</p>
            </div>
          ) : latestPlan ? (
            <div className="space-y-3">
              {/* Latest Plan Card */}
              <div className="bg-background border border-border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="default">{latestPlan.durationWeeks} weeks</Badge>
                      <span className="text-sm text-muted-foreground">
                        {latestPlan.startDate} → {latestPlan.endDate}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(latestPlan.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Phases */}
                {latestPlan.phases && Array.isArray(latestPlan.phases) && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-foreground mb-2">Phases</h4>
                    <div className="space-y-2">
                      {(latestPlan.phases as any[]).map((phase: any, idx: number) => (
                        <div key={idx} className="bg-secondary rounded p-3">
                          <div className="font-medium text-foreground">{phase.name || `Phase ${idx + 1}`}</div>
                          <p className="text-sm text-muted-foreground mt-1">{phase.focus || phase.description}</p>
                          {phase.weeks && <p className="text-xs text-muted-foreground mt-1">Weeks: {phase.weeks}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Milestones */}
                {latestPlan.milestones &&
                  Array.isArray(latestPlan.milestones) &&
                  (latestPlan.milestones as any[]).length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-foreground mb-2">Milestones</h4>
                      <div className="space-y-1">
                        {(latestPlan.milestones as any[]).map((milestone: any, idx: number) => (
                          <div key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>•</span>
                            <span>{milestone.name || milestone}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Show count if there are more plans */}
              {quarterlyPlansData && quarterlyPlansData.quarterlyPlans.length > 1 && (
                <p className="text-xs text-muted-foreground text-center">
                  + {quarterlyPlansData.quarterlyPlans.length - 1} more plan
                  {quarterlyPlansData.quarterlyPlans.length > 2 ? "s" : ""} in history
                </p>
              )}
            </div>
          ) : (
            <div className="bg-background border border-border rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-sm text-muted-foreground">
                No quarterly plan yet. Generate one to structure your goals into phases.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quarterly Plan Modal */}
      <QuarterlyPlanModal
        coachId={coach.id}
        coachName={coach.name}
        isOpen={showQuarterlyPlanModal}
        onClose={() => setShowQuarterlyPlanModal(false)}
        onSuccess={handleQuarterlyPlanSuccess}
      />

      {/* Weekly Planning Section */}
      <div className="bg-secondary rounded-lg p-6">
        <h2 className="text-xl font-bold text-foreground mb-4">Weekly Planning</h2>
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Generate just-in-time weekly plans with specific expectations and daily structure.
          </p>

          <div className="flex gap-3">
            <Button onClick={handleGenerateWeeklyPlan} loading={isGeneratingPlan} disabled={coach.status !== "active"}>
              Generate Weekly Plan
            </Button>
            <Button onClick={handleAdjustPlan} variant="outline" disabled={coach.status !== "active"}>
              Adjust Plan
            </Button>
          </div>

          {coach.status !== "active" && (
            <p className="text-sm text-muted-foreground italic">⚠️ Coach must be active to generate plans</p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-secondary rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-muted-foreground">Weekly Plans</p>
          <p className="text-xl md:text-2xl font-bold text-foreground">{weeklyPlansCount}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {weeklyPlansCount === 0 ? "Create your first plan" : "Total plans created"}
          </p>
        </div>
        <div className="bg-secondary rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-muted-foreground">Trackables</p>
          <p className="text-xl md:text-2xl font-bold text-foreground">{trackablesCount}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {trackablesCount === 0 ? "Configure in Settings tab" : "Active trackables"}
          </p>
        </div>
        <div className="bg-secondary rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-muted-foreground">Conversations</p>
          <p className="text-xl md:text-2xl font-bold text-foreground">-</p>
          <p className="text-xs text-muted-foreground mt-1">View in Chat tab</p>
        </div>
      </div>

      {/* Adjust Plan Modal */}
      <AdjustPlanModal
        weeklyPlanId={selectedPlanForAdjustment}
        coachName={coach.name}
        isOpen={!!selectedPlanForAdjustment}
        onClose={() => setSelectedPlanForAdjustment(null)}
        onSuccess={() => {
          refetchCoach()
        }}
      />
    </div>
  )
}
